import { useState, useRef, useEffect, useCallback } from "react";
import {
  trackSearchAbandoned,
  trackSearchInitiated,
  trackSearchResultClicked,
} from "../../../analytics/amplitudeTracking";
import { Spin } from "antd";
import { Input } from "../CustomAntdComponents/Input";
import { SearchIcon } from "../../../assets/icons";
import { useStockSearch } from "../../../hooks/useStockSearch";
import styles from "./StockSearch.module.css";

interface StockSearchProps {
  /**
   * Width of the search input
   * @default "320px"
   */
  width?: string | number;
  /**
   * Placeholder text
   * @default "Search Stocks & ETFs"
   */
  placeholder?: string;
  /**
   * Label for a brand-colored action joined to the field.
   * Empty query focuses the field; a query with results selects the
   * highlighted match, same as pressing Enter.
   */
  actionLabel?: string;
  /**
   * Callback when a stock is selected
   */
  onSelect?: (symbol: string) => void;
  /**
   * Debounce delay in milliseconds
   * @default 300
   */
  debounceMs?: number;
  /**
   * Custom className for the AutoComplete component
   */
  className?: string;
  /**
   * Custom className for the Input component
   */
  inputClassName?: string;
  /**
   * Size of the input
   */
  size?: "small" | "middle" | "large";
  /**
   * Custom style for the AutoComplete component
   */
  style?: React.CSSProperties;
  /**
   * Where the search UI lives (for analytics)
   * @default "navbar"
   */
  searchLocation?: string;
}

/**
 * Reusable stock search component with autocomplete using CustomSelect
 */
export const StockSearch: React.FC<StockSearchProps> = ({
  width = "320px",
  placeholder = "Search Stocks & ETFs",
  actionLabel,
  onSelect,
  debounceMs = 300,
  className,
  inputClassName,
  size,
  style,
  searchLocation = "navbar",
}) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchSessionStartedAtRef = useRef<number | null>(null);
  const lastTrackedQueryRef = useRef<string>("");
  const hasSelectedResultRef = useRef(false);
  const { searchValue, searchOptions, isLoading, handleSearch, clearSearch } =
    useStockSearch({
      debounceMs,
      onSelect: (symbol) => {
        onSelect?.(symbol);
        setIsSelectOpen(false);
      },
    });

  useEffect(() => {
    if (searchValue.length === 0) {
      searchSessionStartedAtRef.current = null;
      lastTrackedQueryRef.current = "";
      hasSelectedResultRef.current = false;
      return;
    }

    if (searchSessionStartedAtRef.current === null) {
      searchSessionStartedAtRef.current = Date.now();
      hasSelectedResultRef.current = false;
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue.length === 0 || isLoading) return;
    if (lastTrackedQueryRef.current === searchValue) return;

    lastTrackedQueryRef.current = searchValue;
    trackSearchInitiated({
      search_query: searchValue,
      query_length: searchValue.length,
      search_location: searchLocation,
    });
  }, [searchValue, isLoading, searchLocation]);

  const trackAbandonmentIfNeeded = useCallback(() => {
    if (
      searchValue.length === 0 ||
      hasSelectedResultRef.current ||
      searchSessionStartedAtRef.current === null
    ) {
      return;
    }

    trackSearchAbandoned({
      search_query: searchValue,
      query_length: searchValue.length,
      time_spent_ms: Date.now() - searchSessionStartedAtRef.current,
      search_location: searchLocation,
    });
    searchSessionStartedAtRef.current = null;
    lastTrackedQueryRef.current = "";
  }, [searchValue, searchLocation]);

  // Handle click outside to close select
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        trackAbandonmentIfNeeded();
        setIsSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [trackAbandonmentIfNeeded]);

  // Open select when there are search results and focus first option
  useEffect(() => {
    if (searchOptions.length > 0 && searchValue.length > 0) {
      setIsSelectOpen(true);
      setHighlightedIndex(0); // Focus first option by default
    } else if (searchValue.length === 0) {
      setIsSelectOpen(false);
      setHighlightedIndex(-1);
    }
  }, [searchOptions.length, searchValue.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleSearch(value);
    if (value.length === 0) {
      clearSearch();
    }
  };

  const handleOptionClick = useCallback(
    (value: string, position: number) => {
      const startedAt = searchSessionStartedAtRef.current ?? Date.now();
      hasSelectedResultRef.current = true;
      trackSearchResultClicked({
        search_query: searchValue,
        result_clicked: value,
        result_position: position,
        time_to_click_ms: Date.now() - startedAt,
        search_location: searchLocation,
      });
      onSelect?.(value);
      setIsSelectOpen(false);
      clearSearch();
      searchSessionStartedAtRef.current = null;
      lastTrackedQueryRef.current = "";
    },
    [onSelect, clearSearch, searchValue, searchLocation],
  );

  const handleInputFocus = () => {
    if (searchOptions.length > 0 && searchValue.length > 0) {
      setIsSelectOpen(true);
    }
  };

  const handleActionClick = () => {
    if (!isLoading && searchValue.length > 0 && searchOptions.length > 0) {
      const index =
        highlightedIndex >= 0 && highlightedIndex < searchOptions.length
          ? highlightedIndex
          : 0;
      handleOptionClick(searchOptions[index].value, index + 1);
      return;
    }

    containerRef.current?.querySelector("input")?.focus();
  };

  // Scroll to highlighted option
  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      optionRefs.current[highlightedIndex] &&
      dropdownRef.current
    ) {
      const optionElement = optionRefs.current[highlightedIndex];
      const dropdownElement = dropdownRef.current;
      const optionTop = optionElement.offsetTop;
      const optionHeight = optionElement.offsetHeight;
      const dropdownScrollTop = dropdownElement.scrollTop;
      const dropdownHeight = dropdownElement.clientHeight;

      // Scroll if option is above visible area
      if (optionTop < dropdownScrollTop) {
        dropdownElement.scrollTo({
          top: optionTop,
          behavior: "smooth",
        });
      }
      // Scroll if option is below visible area
      else if (optionTop + optionHeight > dropdownScrollTop + dropdownHeight) {
        dropdownElement.scrollTo({
          top: optionTop + optionHeight - dropdownHeight,
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSelectOpen || searchOptions.length === 0) {
      if (e.key === "ArrowDown" && searchOptions.length > 0) {
        setIsSelectOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < searchOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchOptions.length) {
          handleOptionClick(
            searchOptions[highlightedIndex].value,
            highlightedIndex + 1,
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        trackAbandonmentIfNeeded();
        setIsSelectOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className || ""}`}
      style={{ width, ...style }}
    >
      <div className={actionLabel ? styles.field : undefined}>
        <div className={styles.inputSlot}>
          <Input
            prefix={
              actionLabel ? undefined : isLoading ? (
                <Spin size="small" />
              ) : (
                <SearchIcon />
              )
            }
            placeholder={placeholder}
            className={
              actionLabel
                ? styles.groupedInput
                : inputClassName || styles.searchInput
            }
            size={size}
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
          />
        </div>
        {actionLabel ? (
          <button
            type="button"
            className={styles.action}
            aria-label={actionLabel}
            onClick={handleActionClick}
          >
            {isLoading ? <Spin size="small" /> : <SearchIcon />}
          </button>
        ) : null}
      </div>
      {isSelectOpen && searchValue.length > 0 && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {isLoading ? (
            <div className={styles.loading}>Searching...</div>
          ) : searchOptions.length === 0 ? (
            <div className={styles.empty}>No stocks found</div>
          ) : (
            searchOptions.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                className={styles.optionWrapper}
                onMouseDown={() => {
                  hasSelectedResultRef.current = true;
                }}
                onClick={() => handleOptionClick(option.value, index + 1)}
              >
                <div
                  className={`${styles.option} ${
                    index === highlightedIndex ? styles.optionActive : ""
                  }`}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
